"use client";

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import styled from "styled-components";
import IconTourOperator from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconTourOperator.js";
import MultiSelect from "@/components/UI/MultiSelect/MultiSelect";
import SearchableCheckboxList from "@/components/UI/SearchableCheckboxList/SearchableCheckboxList";
import useTourOperator from "@/app/api/useTourOperator";
import TourOperator from "@/lib/Types/TourOperator";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";
import { AppConfig } from "@/lib/Types/AppConfig";

const FilterWrapper = styled.div<{ $expanded?: boolean }>(({ $expanded }) => ({
  width: "100%",
  marginTop: $expanded ? 0 : "1rem",
}));

interface TourOperatorFilterProps {
  delayedUpdate?: boolean;
  expanded?: boolean;
  appConfig: AppConfig;
}

const TourOperatorFilter: React.FC<TourOperatorFilterProps> = ({
  delayedUpdate = false,
  expanded = false,
  appConfig,
}) => {
  const { envConfig, viewMode } = appConfig;
  const searchParams = useReactiveSearchParams();
  const { tempFilters, updateFilter, updateTempFilterArray } = useFilterState();

  // Fetch tour operators data
  const { data, isLoading } = useTourOperator({
    envConfig: envConfig,
    viewMode: viewMode,
    travelStartDate: searchParams.get("from") ?? "",
    transactionId: searchParams.get("transactionId") ?? "",
    searchId: searchParams.get("searchId") ?? ""
  });

  // Initialize state from either URL params or temp filters based on delayedUpdate
  const [selectedOperators, setSelectedOperators] = useState<string[]>(() => {
    return delayedUpdate
      ? tempFilters.get("tourOperator")?.split(",") ?? []
      : searchParams.get("tourOperator")?.split(",") ?? [];
  });

  // Sync with URL changes
  useEffect(() => {
    setSelectedOperators(searchParams.get("tourOperator")?.split(",") ?? []);
  }, [searchParams]);

  const handleOperatorChange = useCallback(
    (newSelectedOperators: string[]) => {
      setSelectedOperators(newSelectedOperators);

      if (delayedUpdate) {
        // Update temp filters for mobile dialog
        updateTempFilterArray("tourOperator", newSelectedOperators);
      } else {
        // Immediate URL update for desktop view
        updateFilter(
          "tourOperator",
          newSelectedOperators.length > 0
            ? newSelectedOperators.join(",")
            : null
        );
      }
    },
    [updateFilter, updateTempFilterArray, delayedUpdate]
  );

  // Convert tour operators data to options format
  const options = useMemo(
    () =>
      data && data.length > 0
        ? data
          .map((operator: TourOperator) => ({
            value: operator.code,
            label: operator.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
        : [],
    [data]
  );

  return (
    <FilterWrapper data-testid="tourOperatorFilter" $expanded={expanded}>
      {expanded ? (
        <SearchableCheckboxList
          options={options}
          value={selectedOperators}
          onChange={handleOperatorChange}
          delayedUpdate={delayedUpdate}
          searchLabel="Reiseveranstalter suchen"
          listAriaLabel="Reiseveranstalter"
          emptyStateText="Keine Reiseveranstalter gefunden"
          formatResultCount={(filtered, total) =>
            `${filtered} von ${total} Reiseveranstaltern`
          }
          testIdPrefix="tourOperator"
          loading={isLoading}
        />
      ) : (
        <MultiSelect
          options={options}
          value={selectedOperators}
          onChange={handleOperatorChange}
          placeholder="Beliebig"
          label="Name des Veranstalters"
          icon={<IconTourOperator size={24} aria-hidden="true" />}
          aria-label="Wählen Sie einen oder mehrere Reiseveranstalter"
          aria-describedby="tour-operator-description"
          dataTestId="tourOperatorList"
          loading={isLoading}
        />
      )}
      <span id="tour-operator-description" className="sr-only">
        Wählen Sie einen oder mehrere Reiseveranstalter aus der Liste aus.
        Verwenden Sie die Pfeiltasten, um durch die Optionen zu navigieren, die
        Leertaste zum Auswählen und Enter zum Bestätigen.
      </span>
    </FilterWrapper>
  );
};

export default TourOperatorFilter;
